<script>
  import { createEventDispatcher } from 'svelte';
  import { auth } from '../../stores/auth';
  import { navigate } from 'svelte-routing';

  const dispatch = createEventDispatcher();

  let username = '';
  let password = '';
  let confirmPassword = '';
  let loading = false;
  let error = '';

  function validate() {
    // Trim whitespace
    username = username.trim();

    if (username.length < 3) {
      error = 'Username must be at least 3 characters';
      return false;
    }
    if (password.length < 8) {
      error = 'Password must be at least 8 characters';
      return false;
    }
    if (password !== confirmPassword) {
      error = 'Passwords do not match';
      return false;
    }
    error = '';
    return true;
  }

  async function handleSubmit() {
    if (loading || !validate()) return;

    loading = true;
    error = '';

    try {
      const response = await auth.register(username, password);
      if (response?.success || response?.access) {
        password = '';
        confirmPassword = '';
        dispatch('success');
        await navigate('/', { replace: true });
      } else {
        throw new Error('Registration failed');
      }
    } catch (e) {
      console.error('Registration error:', e);
      // Provide user-friendly error messages
      if (e.message.includes('409') || e.message.includes('already exists')) {
        error = 'Username already exists. Please choose another.';
      } else if (e.message.includes('400')) {
        error = 'Invalid registration details. Please check your input.';
      } else if (e.message.includes('Network')) {
        error = 'Network error. Please check your connection.';
      } else {
        error = e.message || 'Registration failed. Please try again.';
      }
      password = '';
      confirmPassword = '';
    } finally {
      loading = false;
    }
  }
</script>

<form on:submit|preventDefault={handleSubmit}>
  <div class="field">
    <label for="reg-username">Username</label>
    <input
      type="text"
      id="reg-username"
      bind:value={username}
      placeholder="Choose a username"
      disabled={loading}
      required
      minlength="3"
    />
  </div>

  <div class="field">
    <label for="reg-password">Password</label>
    <input
      type="password"
      id="reg-password"
      bind:value={password}
      placeholder="Choose a password"
      disabled={loading}
      required
      minlength="8"
    />
  </div>

  <div class="field">
    <label for="reg-confirm">Confirm Password</label>
    <input
      type="password"
      id="reg-confirm"
      bind:value={confirmPassword}
      placeholder="Confirm password"
      disabled={loading}
      required
    />
  </div>

  {#if error}
    <div class="error">{error}</div>
  {/if}

  <button type="submit" disabled={loading || !username || !password || !confirmPassword}>
    {loading ? 'Creating account...' : 'Create Account'}
  </button>
</form>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }

  input {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 15px;
    background: #fff;
    transition: border-color 0.15s, box-shadow 0.15s;
  }

  input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  input:disabled {
    background: #f8fafc;
    cursor: not-allowed;
  }

  .error {
    padding: 12px;
    background: #fef2f2;
    color: #dc2626;
    border-radius: 8px;
    font-size: 13px;
    text-align: center;
  }

  button {
    width: 100%;
    padding: 14px;
    background: #3b82f6;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }

  button:hover:not(:disabled) {
    background: #2563eb;
  }

  button:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
</style>
